// PHA-626: the onboarding script as opened from the patient chart (Actions menu), at any
// stage. It uses whatever the chart has: the approved care plan, else the draft, else only
// the problem and medication lists. The care plan is optional because the program is often
// offered before one exists. A DRAFT is the patient's care plan for the script (Zack,
// 2026-11-03): callers must pass every draft (ehrOnly=false), not just EHR-refresh ones.
import type { PatientFriendlyPlan } from '../types/patient';
import { deriveManagedConditionNames } from './carePlanDisplay';
import { deriveCarePlanPrograms } from './generateCarePlanPdf';
import {
  OnboardingScript,
  OnboardingScriptTemplate,
  buildOnboardingScript,
  pickPatientFriendlyPlan,
} from './onboardingScript';

interface ScriptCarePlanDefinition {
  disease: string;
  diseases?: string[];
  is_general?: boolean;
  icd10_codes?: { code: string; description: string; clinical_disease?: string | null }[];
  patient_friendly_plan?: PatientFriendlyPlan | null;
}

export interface PatientOnboardingScriptSource {
  patient: {
    first_name?: string | null;
    apcm_tier?: number | null;
    has_rpm_device?: boolean | null;
    problems?: { problem?: string | null; status?: string | null }[] | null;
    medications?: { name?: string | null; status?: string | null }[] | null;
  };
  approvedDefinitions?: ScriptCarePlanDefinition[] | null;
  draftDefinitions?: ScriptCarePlanDefinition[] | null;
  /** The tier the draft would give (patients.apcm_tier is the LAST approval's). */
  draftApcmTier?: number | null;
  template?: OnboardingScriptTemplate | null;
  clinicName?: string | null;
}

export type OnboardingScriptSource = 'care_plan' | 'draft' | 'none';

export interface PatientOnboardingScriptResult {
  script: OnboardingScript;
  source: OnboardingScriptSource;
  /** Shown above the script when some sections fall back to general wording. */
  notice: string | null;
}

const INACTIVE_STATUS = /resolved|inactive|discontinued|stopped|completed|historical/i;

const isActive = (status?: string | null) => !status || !INACTIVE_STATUS.test(status);

export function buildPatientOnboardingScript(src: PatientOnboardingScriptSource): PatientOnboardingScriptResult {
  const approved = src.approvedDefinitions ?? [];
  const draft = src.draftDefinitions ?? [];
  const source: OnboardingScriptSource = approved.length > 0 ? 'care_plan' : draft.length > 0 ? 'draft' : 'none';
  const defs = source === 'care_plan' ? approved : source === 'draft' ? draft : [];
  const tier = source === 'draft' && src.draftApcmTier !== undefined
    ? src.draftApcmTier
    : (src.patient.apcm_tier ?? null);
  const pfp = pickPatientFriendlyPlan(defs);
  const problems = (src.patient.problems ?? []).filter((p) => isActive(p.status));
  const medications = (src.patient.medications ?? []).filter((m) => (m.name ?? '').trim() && isActive(m.status));

  const script = buildOnboardingScript({
    programs: deriveCarePlanPrograms({ apcm_tier: tier, has_rpm_device: src.patient.has_rpm_device }),
    generalOnly: tier === 1,
    firstName: src.patient.first_name,
    conditionNames: source === 'none' ? [] : deriveManagedConditionNames({ care_plan_definitions: defs }),
    patientFriendlyPlan: pfp,
    hasCarePlan: source !== 'none',
    problemNames: problems.map((p) => p.problem ?? ''),
    medicationCount: medications.length,
    clinicName: src.clinicName,
    template: src.template,
  });

  // With no care plan at all the clinician brief above the script already says so.
  const notice = source !== 'none' && !pfp
    ? 'A plain-English care plan has not been generated for this patient, so some sections use general wording.'
    : null;
  return { script, source, notice };
}
